import { runBatch } from '../../../utils/office/document';
import { splitEdgeSpacing } from '../../../utils/office/selection';

/** Quotation marks a defined term is wrapped in. German is the default (see
 *  below); exported so the pane button can show the term the way it lands in
 *  the document. */
export const DEFINITION_QUOTES = {
  de: { open: '„', close: '“' }, // „Term“
  en: { open: '“', close: '”' }, // “Term”
} as const;

/**
 * How the quoted term is enclosed. Contracts introduce a term either bare, in
 * parentheses, or in parentheses with the article — never with the term itself
 * losing its bold, hence one place for all three.
 */
export type DefinedTermVariant = 'plain' | 'parens' | 'parensWithArticle';

/**
 * The regular-weight text that goes around the bold term for each variant.
 * Shared by the insert and the pane button preview, so the button always shows
 * what the document gets.
 */
export function definedTermAffixes(
  variant: DefinedTermVariant,
  quotes: { open: string; close: string } = DEFINITION_QUOTES.de,
): { open: string; close: string } {
  const article = variant === 'parensWithArticle' ? 'the ' : '';
  const parens = variant !== 'plain';
  return {
    open: `${parens ? '(' : ''}${article}${quotes.open}`,
    close: `${quotes.close}${parens ? ')' : ''}`,
  };
}

/**
 * Wraps the text in quotation marks (language-dependent), makes it bold, and
 * puts a hidden XE index field inside the quotes, directly before the closing
 * one: „Term⟨XE⟩“. With `variant`, the quoted term is additionally wrapped in
 * parentheses — („Term“) or (the „Term“) — with only the term bold.
 * Uses selection if available, otherwise uses textOverride.
 */
export async function addDefinedTerm(
  textOverride?: string,
  variant: DefinedTermVariant = 'plain',
): Promise<void> {
  await runBatch(async (context) => {
    const selection = context.document.getSelection();
    selection.load('text');
    await context.sync();

    // Spacing the user selected along with the term is written back around the
    // quotes instead of being replaced away ("hello„Term“" was the result).
    const { lead, core, trail } = splitEdgeSpacing(selection.text ?? '');
    const selectedText = (textOverride ?? core)?.trim();
    if (!selectedText) {
      throw new Error('No text available.');
    }

    // Detect language for quotation marks — default to German
    let isGerman = true;
    try {
      selection.load('languageId');
      await context.sync();
      const langId = selection.languageId as string;
      isGerman = !langId || langId.startsWith('German') || langId === 'Undefined';
    } catch {
      // If languageId isn't available, default to German
    }

    const openQuote = isGerman ? DEFINITION_QUOTES.de.open : DEFINITION_QUOTES.en.open; // „ or "
    const closeQuote = isGerman ? DEFINITION_QUOTES.de.close : DEFINITION_QUOTES.en.close; // " or "

    // Everything around the term — quotes, parentheses, article — is regular
    // weight; only the TERM itself ends up bold. It is written UNBOLDED here on
    // purpose, see the field insert below.
    const { open: openAffix, close: closeAffix } = definedTermAffixes(variant, {
      open: openQuote,
      close: closeQuote,
    });

    const openRange = selection.insertText(`${lead}${openAffix}`, Word.InsertLocation.replace);
    openRange.font.bold = false;
    const termRange = openRange.insertText(selectedText, Word.InsertLocation.after);
    termRange.font.bold = false;
    // Any spacing the user selected past the term is written back after the
    // closing quote/parenthesis, so the term does not swallow it.
    const closeRange = termRange.insertText(`${closeAffix}${trail}`, Word.InsertLocation.after);
    closeRange.font.bold = false;

    // Commit the term FIRST, in its own sync, so that a failure of the
    // (host-dependent) XE field insert below cannot roll back the term with it.
    await context.sync();

    let fieldFailed = false;
    try {
      const fieldText = `XE "${selectedText.replace(/"/g, '\\"')}"`;
      // The index entry belongs INSIDE the quotation marks, immediately before
      // the closing one: „Term⟨hidden XE⟩“. Inserting AFTER the term range puts
      // it exactly there — it used to sit outside, behind an extra space.
      //
      // A field inherits the formatting at its insertion point, and the XE
      // entry must NOT be bold (a bold entry carries into the generated index).
      // That is why the term is still unbolded at this moment — it is bolded
      // below, which touches its own characters only, not the field's.
      //
      // removeFormatting=true: without it Word appends `\* MERGEFORMAT`, which
      // is meaningless on a field with no visible result (issue #5).
      const field = termRange.insertField(
        Word.InsertLocation.after,
        Word.FieldType.empty,
        fieldText,
        true,
      );
      field.load('result');
      await context.sync();
      field.result.font.bold = false;
    } catch {
      // Only the hidden XE index entry failed (e.g. Word on the web) — the term
      // is already committed. Reported below, after it has been formatted.
      fieldFailed = true;
    }

    // Bold the term itself — always, so a failed field never leaves a
    // half-formatted defined term behind.
    termRange.font.bold = true;
    await context.sync();

    if (fieldFailed) {
      throw new Error(
        'Term inserted, but the XE index entry is not supported by this version of Word (e.g. web).',
      );
    }
  });
}
